/**
 * ConfigSectionShell — carcasa unificada para TODA sección del panel Administrar Cliente.
 *
 * Es la extracción del patrón que ya usaban CatalogadorPanel y DataHealthPanel:
 *   card + header(icono + título + —clientName) + TAB LIST (border-b-2, activa azul)
 *   + descripción de la tab activa (indigo) + panel de contenido (p-4).
 *
 * Objetivo (directiva de catálogo de componentes): que las 8 secciones del config
 * (General, Tomas de Datos, Usuarios, Categorías, Columnas, Tags, Catalogador IA,
 * Data Health) compartan EXACTAMENTE el mismo lenguaje visual. NO crear layouts
 * divergentes por sección — usar SIEMPRE este shell.
 *
 * Ver: docs/design-system/COMPONENT_CATALOG.md
 */
import React from 'react';

export interface SectionTab {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Texto explicativo que aparece en la barra indigo cuando la tab está activa. */
  description?: string;
  /** Si true, la tab no se renderiza (p.ej. gating superadmin). */
  hidden?: boolean;
}

interface Props {
  /** Ícono del título de la sección. DEBE coincidir con el ícono del item en el sidebar. */
  icon: React.ComponentType<{ className?: string }>;
  /** Nombre de la sección. DEBE coincidir con el label del item en el sidebar. */
  title: string;
  clientName?: string;
  /** Lista de tabs. Si se omite o queda 1 sola, igual se muestra por consistencia. */
  tabs?: SectionTab[];
  activeTab?: string;
  onTabChange?: (id: string) => void;
  /** Slot a la derecha del header (toast, badge de estado, botón global de la sección). */
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}

const ConfigSectionShell: React.FC<Props> = ({
  icon: Icon,
  title,
  clientName,
  tabs,
  activeTab,
  onTabChange,
  headerRight,
  children,
}) => {
  const visibleTabs = (tabs || []).filter((t) => !t.hidden);
  const activeMeta = visibleTabs.find((t) => t.id === activeTab);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-blue-600" />
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {clientName && <span className="text-xs text-gray-400">— {clientName}</span>}
        </div>
        {headerRight && <div className="flex items-center gap-2">{headerRight}</div>}
      </div>

      {/* Tab list — activa azul (mismo patrón que Data Health / Catalogador IA). */}
      {visibleTabs.length > 0 && (
        <div className="flex border-b border-gray-200 px-2 overflow-x-auto">
          {visibleTabs.map((tab) => {
            const TabIcon = tab.icon;
            const active = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTabChange?.(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  active
                    ? 'border-blue-700 text-blue-700 bg-blue-50'
                    : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <TabIcon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Descripción de la tab activa */}
      {activeMeta?.description && (
        <div className="px-4 pt-3">
          <div className="bg-indigo-50 border-l-2 border-indigo-300 rounded px-3 py-2 text-xs text-gray-600">
            {activeMeta.description}
          </div>
        </div>
      )}

      {/* Contenido */}
      <div className="p-4">{children}</div>
    </div>
  );
};

export default ConfigSectionShell;
